import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import Button from 'components/Button';
import { appAuthContext } from 'context/AuthContext';
import getUser from 'api/requests/getUser';
import useClickStreamEvent from 'utils/clickstream/useClickStreamEvent';
import loginRequest from '../../api/loginRequest';
import useFormLogic from '../../hooks/useFormLogic';
import { AuthModalStateProps } from '../../AuthModal.types';
import styles from './EntryScreen.module.css';

const EntryScreen = ({ setModalState, onClose, entryPoint }: AuthModalStateProps) => {
  const {
    login,
    setLogin,
    password,
    setPassword,
    passwordType,
    setPasswordType,
    loginError,
    passwordError,
    validateForm,
    openRegistration,
  } = useFormLogic();
  const { sendEvent } = useClickStreamEvent();

  const [disableButton, setDisableButton] = useState(false);
  const { setUser } = useContext(appAuthContext);
  const router = useRouter();
  const [loginErrorReq, setLoginErrorReq] = useState('');
  const [unsuccessfulAuth, setUnsuccessfulAuth] = useState(false);

  const closeModalBtnHandler = () => {
    if (unsuccessfulAuth) {
      sendEvent({
        eventType: 'click',
        eventInfo: 'otvety_close_after_unsuccessful',
        customDimentions: {
          cd1: JSON.stringify({ source: entryPoint }),
        },
      });
      setUnsuccessfulAuth(false);
    }
    onClose();
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogin(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleEye = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (e.type === 'mousedown' || e.type === 'pointerdown') {
      setPasswordType('text');
    } else {
      setPasswordType('password');
    }
  };

  const setUserInfo = async () => {
    const data = await getUser();
    if (data) {
      setUser({ ...data });
      await router.replace(router.asPath);
      onClose();
    }
  };

  const sendAuthForm = async () => {
    setLoginErrorReq('');
    if (!validateForm()) {
      return;
    }
    setDisableButton(true);
    const response = await loginRequest(login, password);
    setDisableButton(false);
    if (response.success) {
      await Promise.all([setUserInfo()]);
      // Удаление параметра auth=true при успешной авторизации
      if (unsuccessfulAuth) {
        await sendEvent({
          eventType: 'click',
          eventInfo: 'otvety_auth_after_unsuccessful',
          customDimentions: {
            cd1: JSON.stringify({ source: entryPoint }),
          },
        });
      }
      await sendEvent({
        eventType: 'click',
        eventInfo: 'otvety_auth_success',
        customDimentions: {
          cd1: JSON.stringify({ source: entryPoint }),
        },
      });
    } else if (response.error) {
      setLoginErrorReq(response.error);
      setUnsuccessfulAuth(true);
      sendEvent({
        eventType: 'click',
        eventInfo: 'otvety_auth_error',
        customDimentions: {
          cd1: JSON.stringify({ source: entryPoint }),
        },
      });
    }
  };

  const handleEnterClick = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      await sendAuthForm();
    }
  };

  const successRegistration = async () => {
    await sendEvent({
      eventType: 'click',
      eventInfo: 'otvety_reg_success',
      customDimentions: {
        cd1: JSON.stringify({ source: entryPoint }),
      },
    });
    setUserInfo();
  };

  const registrationBtnHandler = () => {
    sendEvent({
      eventType: 'click',
      eventInfo: 'otvety_reg_open',
      customDimentions: {
        cd1: JSON.stringify({ source: entryPoint }),
      },
    });
    openRegistration(successRegistration);
  };

  return (
    <div className={styles.entry_wrapper}>
      <div className={styles.header}>
        <span
          className={styles.icon_close}
          onClick={closeModalBtnHandler}
          data-qa-marker="close_login_form_btn"
        />
      </div>
      <div className={styles.gray_logo} />

      <div className={styles.content_container}>
        {entryPoint === 'question' ? (
          <>
            <h2 className={styles.title}>Войди, чтобы вопрос опубликовался</h2>
          </>
        ) : entryPoint === 'answer' ? (
          <>
            <h2 className={styles.title}>Войди, чтобы ответ опубликовался</h2>
          </>
        ) : entryPoint === 'open_ai_answer' ? (
          <>
            <h2 className={styles.title}>Для помощи Маруси войди на Учи.ру</h2>
          </>
        ) : (
          <>
            <h2 className={styles.title}>Вход</h2>
          </>
        )}

        <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            id="login"
            className={`${styles.input} ${
              loginError || loginErrorReq ? styles.input_error : ''
            } ${loginErrorReq ? styles.input_mb : ''}`}
            value={login}
            onChange={handleLoginChange}
            onKeyDown={handleEnterClick}
            placeholder={'Логин'}
            data-qa-marker="login_input"
          />
          {loginError && (
            <label htmlFor="login" className={styles.label_error}>
              {loginError}
            </label>
          )}
          <div className={styles.password_container}>
            <input
              type={passwordType}
              id="password"
              className={`${styles.input} ${
                passwordError || loginErrorReq ? styles.input_error : ''
              }`}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={handleEnterClick}
              placeholder={'Пароль'}
              data-qa-marker="pass_input"
            />
            {(passwordError || loginErrorReq) && (
              <label htmlFor="" className={styles.label_error}>
                {passwordError || loginErrorReq}
              </label>
            )}
            <button
              className={styles.password_eye}
              id="password_eye"
              onMouseUp={handleEye}
              onMouseDown={handleEye}
              onMouseOut={handleEye}
              onPointerUp={handleEye}
              onPointerDown={handleEye}
              onPointerOut={handleEye}
              data-qa-marker="show_pwd_btn"
            />
          </div>
          <div className={styles.footer}>
            <Button
              text={'Войти'}
              onClick={sendAuthForm}
              addStyles={{ fontSize: 15, padding: '15px 25px 16px' }}
              disabled={disableButton}
              qaMarker="entry_btn"
            />
            <p
              className={styles.remind_password}
              onClick={() => setModalState('remind')}
              data-qa-marker="reset_pwd_btn"
            >
              Напомнить пароль
            </p>
          </div>
        </form>
      </div>
      <p className={styles.subtitle}>
        Впервые на Учи.ру?&nbsp;
        <span
          className={styles.registration_link}
          onClick={registrationBtnHandler}
          data-qa-marker="registration_btn"
        >
          Регистрация
        </span>
      </p>
    </div>
  );
};

export default EntryScreen;
